import { colors, Spacer } from '@spritz-finance/ui'
import { motion } from 'framer-motion'
import React, { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useScreenSize } from '../../context/useScreenSize'
import { IRoute } from '../../modules/bills/BillsPage'
import { useElementSize } from '../../utils/useElementSize'
import { Tab } from './Tab'
import { TabBarContainer } from './TabBarContainter'

const Slider = styled(motion.div)`
    height: 100%;
    border-radius: 30px;
    position: absolute;
    background: var(--tab-pill-background, ${colors.brand});
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.16);
`

const TabBarInner = styled.div`
    position: relative;
    display: flex;
`

const useSlidingTab = (value: number) => {
    const [containerRef, bounds] = useElementSize()
    const [slider, setSlider] = useState({ left: 0, right: 0, hasValue: false })
    const tabRefs = useRef<Map<number, HTMLDivElement | null>>(new Map())
    const tabWrapperRef = useRef<HTMLDivElement>(null)

    React.useEffect(() => {
        const target = tabRefs.current.get(value)
        const container = tabWrapperRef.current

        if (target) {
            const cRect = container?.getBoundingClientRect()

            if (!cRect || cRect.width === 0) {
                return
            }

            const tRect = target.getBoundingClientRect()
            const left = tRect.left - cRect.left
            const right = cRect.right - tRect.right

            setSlider({
                hasValue: true,
                left: left,
                right: right,
            })
        }
    }, [value, bounds])

    return { slider, containerRef, tabWrapperRef, tabRefs }
}

export const TabBar = ({
    routes,
    subRoute,
    disabled = false,
}: {
    routes: IRoute[]
    subRoute?: string
    disabled?: boolean
}) => {
    const navigate = useNavigate()
    const value = disabled ? 0 : routes.findIndex((route: IRoute) => route.path === subRoute)
    const { slider, containerRef, tabWrapperRef, tabRefs } = useSlidingTab(value)
    const { isMobile } = useScreenSize()

    return (
        <TabBarContainer ref={containerRef}>
            <TabBarInner ref={tabWrapperRef}>
                {routes.map((route, index) => (
                    <React.Fragment key={index}>
                        <Tab
                            ref={(el) => tabRefs.current.set(index, el)}
                            text={isMobile ? route?.mobileText ?? route.text : route.text}
                            key={route.text}
                            onClick={() => {
                                if (!disabled) navigate(route.path)
                            }}
                            active={route.path === subRoute}
                        />
                        {index !== routes.length - 1 && <Spacer width={16} />}
                    </React.Fragment>
                ))}
                {slider.hasValue && (
                    <Slider
                        layout
                        initial={false}
                        style={{
                            left: slider.left,
                            right: slider.right,
                        }}
                    />
                )}
            </TabBarInner>
        </TabBarContainer>
    )
}
